import React, { useCallback, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { AgentDefinition, ChatMessage, FeedbackRating, PatternCardData, ToolCall } from '../types';
import { MarkdownContent } from './MarkdownContent';

/* ---------- Tool calls summary (after response) ---------- */

const ToolCallsSummary: React.FC<{ tools: ToolCall[] }> = ({ tools }) => {
  const unique = tools.filter((t, i, arr) => arr.findIndex((x) => x.name === t.name) === i);
  return (
    <div className="chat-tools-summary">
      <span className="chat-tools-summary__label">Tools used</span>
      <div className="chat-tools-summary__pills">
        {unique.map((tool, i) => (
          <span
            key={i}
            className={`chat-tools-summary__pill chat-tools-summary__pill--${tool.status}`}
            title={tool.description}
          >
            {tool.name}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- Pattern card ---------- */

const PatternCard: React.FC<{ card: PatternCardData }> = ({ card }) => (
  <div className="chat-pattern-card">
    <div className="chat-pattern-card__title">🗂 {card.title}</div>
    {card.fields.map((f) => (
      <div key={f.label} className="chat-pattern-card__field">
        <span className="chat-pattern-card__label">{f.label}:</span>
        <span className="chat-pattern-card__value">{f.value}</span>
      </div>
    ))}
  </div>
);

/* ---------- Single message bubble ---------- */

interface MessageBubbleProps {
  message: ChatMessage;
  agents: AgentDefinition[];
  userInitials: string;
  onFeedback?: (messageId: string, rating: FeedbackRating) => Promise<void> | void;
}

// Memoised: the parent re-renders every frame while streaming, and without this
// every historical bubble re-runs markdown parsing on each of those renders.
const MessageBubble = React.memo(function MessageBubble({
  message,
  agents,
  userInitials,
  onFeedback,
}: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const agent = agents.find((a) => a.id === message.agentId);
  const [pending, setPending] = useState<FeedbackRating | null>(null);
  const currentRating = message.feedback?.rating ?? null;

  const handleClick = async (rating: FeedbackRating) => {
    if (!onFeedback || pending) return;
    setPending(rating);
    try {
      await onFeedback(message.id, rating);
    } finally {
      setPending(null);
    }
  };

  if (isUser) {
    return (
      <div className="chat-msg chat-msg--user">
        <div className="chat-msg__body chat-msg__body--user">
          <div className="chat-msg__bubble chat-msg__bubble--user">{message.content}</div>
          <div className="chat-msg__time">{message.timestamp}</div>
        </div>
        <div className="chat-msg__avatar chat-msg__avatar--user" title="You">{userInitials}</div>
      </div>
    );
  }

  const isOptimistic = message.id.startsWith('optimistic-');

  return (
    <div className="chat-msg chat-msg--assistant">
      <div
        className="chat-msg__avatar"
        style={{ background: agent?.color ?? '#8b949e' }}
      >
        <span>{agent?.icon ?? '🤖'}</span>
      </div>
      <div className="chat-msg__body">
        {message.patternCard && <PatternCard card={message.patternCard} />}
        {message.content ? (
          <MarkdownContent
            source={message.content}
            className="chat-msg__bubble chat-msg__bubble--assistant chat-msg__bubble--markdown"
          />
        ) : !isOptimistic && (
          <div className="chat-msg__bubble chat-msg__bubble--assistant chat-msg__bubble--empty">
            <span className="chat-msg__empty-label">
              {message.toolCalls && message.toolCalls.length > 0
                ? '⚙️ Agent completed tool calls but returned no text response.'
                : '(No response content)'}
            </span>
          </div>
        )}
        <div className="chat-msg__footer">
          <div className="chat-msg__footer-left">
            <div className="chat-msg__time">{message.timestamp}</div>
            {!isOptimistic && onFeedback && (
              <div className="chat-msg__feedback" role="group" aria-label="Rate this response">
                <button
                  type="button"
                  className={`chat-msg__feedback-btn${currentRating === 'up' ? ' chat-msg__feedback-btn--active' : ''}`}
                  onClick={() => handleClick('up')}
                  disabled={!!pending}
                  aria-pressed={currentRating === 'up'}
                  aria-label="Mark as helpful"
                  title="Helpful"
                >
                  {currentRating === 'up' ? '👍' : '👍🏻'}
                </button>
                <button
                  type="button"
                  className={`chat-msg__feedback-btn${currentRating === 'down' ? ' chat-msg__feedback-btn--active' : ''}`}
                  onClick={() => handleClick('down')}
                  disabled={!!pending}
                  aria-pressed={currentRating === 'down'}
                  aria-label="Mark as not helpful"
                  title="Not helpful"
                >
                  {currentRating === 'down' ? '👎' : '👎🏻'}
                </button>
              </div>
            )}
          </div>
          {message.toolCalls && message.toolCalls.length > 0 && (
            <ToolCallsSummary tools={message.toolCalls} />
          )}
        </div>
      </div>
    </div>
  );
});

/* ---------- Message thread ---------- */

interface MessageThreadProps {
  messages: ChatMessage[];
  agents: AgentDefinition[];
  threadRef: React.RefObject<HTMLDivElement>;
  sending?: boolean;
  streamingContent?: string;
  streamingElapsed?: number | null;
  streamingThinking?: string;
  streamingAgentId?: string | null;
  streamingTools?: ToolCall[];
  onFeedback?: (messageId: string, rating: FeedbackRating) => Promise<void> | void;
  hasMore?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
}

const LOAD_OLDER_SCROLL_THRESHOLD_PX = 120;

export const MessageThread: React.FC<MessageThreadProps> = ({
  messages,
  agents,
  threadRef,
  sending,
  streamingContent,
  streamingElapsed,
  streamingThinking,
  streamingAgentId,
  streamingTools,
  onFeedback,
  hasMore,
  loadingOlder,
  onLoadOlder,
}) => {
  const { accounts } = useMsal();
  const userInitials = useMemo(() => {
    const name = accounts[0]?.name ?? accounts[0]?.username ?? 'U';
    return name
      .split(/[\s.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p: string) => p[0].toUpperCase())
      .join('');
  }, [accounts]);

  const streamingAgent = streamingAgentId ? agents.find((a) => a.id === streamingAgentId) : undefined;

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || loadingOlder || !onLoadOlder) return;
      if (e.currentTarget.scrollTop <= LOAD_OLDER_SCROLL_THRESHOLD_PX) onLoadOlder();
    },
    [hasMore, loadingOlder, onLoadOlder],
  );

  if (!messages.length && !sending) {
    return (
      <div className="chat-thread chat-thread--empty">
        <div className="chat-thread__empty-icon">💬</div>
        <p>Send a message to start the conversation.</p>
      </div>
    );
  }

  return (
    <div className="chat-thread" ref={threadRef} onScroll={handleScroll}>
      {(hasMore || loadingOlder) && (
        <div className="chat-thread__older">
          {loadingOlder ? (
            <span className="chat-thread__older-loading">
              <span className="chat-thread__older-spinner" />
              Loading earlier messages…
            </span>
          ) : (
            <button
              type="button"
              className="chat-thread__older-btn"
              onClick={onLoadOlder}
              disabled={!onLoadOlder}
            >
              ↑ Load earlier messages
            </button>
          )}
        </div>
      )}

      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} agents={agents} userInitials={userInitials} onFeedback={onFeedback} />
      ))}

      {/* Live streaming bubble — shown while agent is working */}
      {sending && (
        <div className="chat-msg chat-msg--assistant">
          <div
            className="chat-msg__avatar"
            style={{ background: streamingAgent?.color ?? '#8b949e' }}
          >
            <span>{streamingAgent?.icon ?? '🤖'}</span>
          </div>
          <div className="chat-msg__body chat-msg__body--stream">

            {/* Live tool-call activity feed */}
            {streamingTools && streamingTools.length > 0 && (
              <div className="stream-activity">
                <div className="stream-activity__header">
                  <span className="stream-activity__pulse" />
                  Agent working…
                </div>
                <div className="stream-activity__steps">
                  {streamingTools.map((tool, i) => (
                    <div key={i} className={`stream-activity__step stream-activity__step--${tool.status}`}>
                      <span className="stream-activity__step-icon">
                        {tool.status === 'running' ? (
                          <span className="stream-activity__spinner" />
                        ) : tool.status === 'success' ? '✓' : '✗'}
                      </span>
                      <span className="stream-activity__step-name">{tool.name}</span>
                      {tool.result && (
                        <span className="stream-activity__step-result">{tool.result}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reasoning / thinking preview — cleared once tokens start arriving */}
            {!streamingContent && streamingThinking && (
              <div className="stream-thinking">{streamingThinking}</div>
            )}

            {/* Streaming text tokens */}
            {streamingContent ? (
              <MarkdownContent
                source={streamingContent}
                className="chat-msg__bubble chat-msg__bubble--assistant chat-msg__bubble--markdown chat-msg__bubble--streaming"
              />
            ) : !streamingTools?.length && !streamingThinking && (
              /* No tools, no thinking, no content yet — typing dots, plus an
                 elapsed hint once the agent has been working a while, so a long
                 analysis reads as progress rather than a stalled spinner. */
              <div className="chat-msg__bubble chat-msg__bubble--assistant chat-msg__typing">
                <span /><span /><span />
                {streamingElapsed !== null && streamingElapsed !== undefined && streamingElapsed >= 10 && (
                  <span className="chat-msg__typing-elapsed">
                    Still working&hellip; {streamingElapsed}s
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
